import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Input, Label } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import {
  checkKbConnection,
  updateKnowledgeBase,
  type KnowledgeBaseUpdateInput,
} from "@/lib/api/endpoints";
import { useEmbeddingModels, useVectorStores } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { KnowledgeBaseView } from "@/types/api";

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

type CheckState = { status: "idle" | "checking" | "ok" | "fail"; error?: string };

/** Edit a knowledge base: rename, swap the embedding model or vector store,
 *  adjust the table scope, or rotate the source credential. Blank = keep. */
export function KbEditDialog({
  open,
  onOpenChange,
  kb,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kb: KnowledgeBaseView;
}) {
  const qc = useQueryClient();

  const [name, setName] = React.useState(kb.name);
  const [embeddingModelSlug, setEmbeddingModelSlug] = React.useState(kb.embedding_model_slug ?? "");
  const embeddingModels = useEmbeddingModels();
  const [allowedTables, setAllowedTables] = React.useState((kb.allowed_tables ?? []).join(", "));
  const [vectorStoreSlug, setVectorStoreSlug] = React.useState(kb.vector_store_slug ?? "");
  const vectorStores = useVectorStores();
  const [sourceDsn, setSourceDsn] = React.useState("");
  const [check, setCheck] = React.useState<CheckState>({ status: "idle" });

  // Re-hydrate from the KB each time the dialog opens.
  React.useEffect(() => {
    if (!open) return;
    setName(kb.name);
    setEmbeddingModelSlug(kb.embedding_model_slug ?? "");
    setVectorStoreSlug(kb.vector_store_slug ?? "");
    setAllowedTables((kb.allowed_tables ?? []).join(", "));
    setSourceDsn("");
    setCheck({ status: "idle" });
  }, [open, kb]);

  const parseTables = (): string[] | null => {
    const items = allowedTables
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    return items.length ? items : null;
  };

  const modelChanged = embeddingModelSlug !== (kb.embedding_model_slug ?? "");
  const storeChanged = vectorStoreSlug !== (kb.vector_store_slug ?? "");
  const tablesChanged = JSON.stringify(parseTables()) !== JSON.stringify(kb.allowed_tables ?? null);
  const willReingest = modelChanged || storeChanged || tablesChanged;

  const testConn = useMutation({
    mutationFn: () => checkKbConnection(sourceDsn.trim()),
    onMutate: () => setCheck({ status: "checking" }),
    onSuccess: (r) =>
      setCheck(r.ok ? { status: "ok" } : { status: "fail", error: r.error ?? "Connection failed" }),
    onError: (e: Error) => setCheck({ status: "fail", error: e.message }),
  });

  const save = useMutation({
    mutationFn: () => {
      const body: KnowledgeBaseUpdateInput = {
        name: name.trim(),
        embedding_model_slug: embeddingModelSlug || undefined,
        vector_store_slug: vectorStoreSlug || undefined,
        allowed_tables: parseTables(),
      };
      // Credentials: rotate only when a new value is entered.
      if (sourceDsn.trim()) body.source_dsn = sourceDsn.trim();
      return updateKnowledgeBase(kb.slug, body);
    },
    onSuccess: async () => {
      toast.success(
        willReingest
          ? "Saved. The model, vector store or table scope changed — re-ingest."
          : "Knowledge base updated.",
      );
      await qc.invalidateQueries({ queryKey: qk.knowledgeBase(kb.slug) });
      await qc.invalidateQueries({ queryKey: qk.knowledgeBases });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSave = !!name.trim() && !save.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit “{kb.name}”</DialogTitle>
          <DialogDescription>
            Rename, change the embedding model, adjust the table scope, or rotate credentials. Leave
            a credential blank to keep the current one.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) save.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="kbe-name">Name</Label>
            <Input id="kbe-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>

          {/* --- Embeddings --- */}
          <fieldset className="space-y-3 rounded-md border border-border p-4">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Embeddings — OpenAI
            </legend>
            <div className="space-y-1.5">
              <Label htmlFor="kbe-model">Embedding model</Label>
              <select
                id="kbe-model"
                className={selectClass}
                value={embeddingModelSlug}
                onChange={(e) => setEmbeddingModelSlug(e.target.value)}
              >
                {(embeddingModels.data ?? []).map((m) => (
                  <option key={m.slug} value={m.slug}>
                    {m.name} — {m.model}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Rotate the API key on the model itself under Embedding models — the key is shared by
                every knowledge base using it, so rotating there covers them all.
              </p>
            </div>
          </fieldset>

          {/* --- Vector store --- */}
          <fieldset className="space-y-3 rounded-md border border-border p-4">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Vector store
            </legend>
            <div className="space-y-1.5">
              <Label htmlFor="kbe-vstore">Store</Label>
              <select
                id="kbe-vstore"
                className={selectClass}
                value={vectorStoreSlug}
                onChange={(e) => setVectorStoreSlug(e.target.value)}
              >
                {kb.vector_store_slug ? null : <option value="">Select a vector store…</option>}
                {(vectorStores.data ?? []).map((s) => (
                  <option key={s.slug} value={s.slug}>
                    {s.name} — {s.provider}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Collection <code className="font-mono">{kb.vector_collection}</code>. Rotate the
                store&apos;s key under Vector stores — it covers every knowledge base using it.
              </p>
            </div>
          </fieldset>

          {/* --- Source --- */}
          <fieldset className="space-y-3 rounded-md border border-border p-4">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Source — SQL database
            </legend>
            <div className="space-y-1.5">
              <Label htmlFor="kbe-dsn">Connection string (DSN)</Label>
              <div className="flex gap-2">
                <Input
                  id="kbe-dsn"
                  type="password"
                  value={sourceDsn}
                  onChange={(e) => {
                    setSourceDsn(e.target.value);
                    setCheck({ status: "idle" });
                  }}
                  placeholder="•••• stored — enter a new value to rotate"
                  autoComplete="off"
                  className="font-mono text-xs"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => testConn.mutate()}
                  disabled={!sourceDsn.trim() || check.status === "checking"}
                >
                  {check.status === "checking" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Test"
                  )}
                </Button>
              </div>
              {check.status === "ok" ? (
                <p className="flex items-center gap-1.5 text-xs text-[hsl(var(--success))]">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Connection succeeded.
                </p>
              ) : check.status === "fail" ? (
                <p className="flex items-start gap-1.5 text-xs text-destructive">
                  <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {check.error}
                </p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="kbe-tables">Allowed tables</Label>
              <textarea
                id="kbe-tables"
                className="flex min-h-[64px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={allowedTables}
                onChange={(e) => setAllowedTables(e.target.value)}
                placeholder="customers, orders (blank = all tables)"
              />
              <p className="text-xs text-muted-foreground">
                Tables in the <code>public</code> schema. The database role&apos;s grants are the
                real boundary — use a role that can only <code>SELECT</code> these tables.
              </p>
            </div>
          </fieldset>

          {willReingest ? (
            <p className="rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs text-foreground">
              Changing the embedding model, vector store or table scope invalidates the stored
              vectors — the knowledge base will return to <span className="font-medium">draft</span>{" "}
              and must be re-ingested.
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave}>
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
